import { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Container,
  Group,
  Paper,
  Progress,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import { FileDropzone } from './components/FileDropzone';
import { TabNavigation } from './components/nutrition/TabNavigation';
import { TabContent } from './components/nutrition/TabContent';
import { tabMetadata } from './data/tab-config';
import { buildTabConfigs, collectTargetRsids } from './data/rules';
import { useGeneticAnalysis } from './hooks/useGeneticAnalysis';

const coverageColor = (pct: number) => {
  if (pct === 100) return 'green';
  if (pct >= 75) return 'blue';
  if (pct >= 50) return 'yellow';
  return 'red';
};

export function AiGeneticsPage() {
  const { state, progress, progressMessage, report, analyzeFile, reset } = useGeneticAnalysis({
    buildTabConfigs,
    collectTargetRsids,
  });

  const [activeTab, setActiveTab] = useState<string>('methylation');

  useEffect(() => {
    if (!report) return;
    const hash = window.location.hash.substring(1);
    const validTabs = report.tabs.map((t) => t.tabId);
    if (hash && validTabs.includes(hash)) {
      setActiveTab(hash);
    } else if (report.tabs.length > 0) {
      setActiveTab(report.tabs[0].tabId);
    }
  }, [report]);

  const handleExportCsv = async () => {
    if (!report) return;
    const { exportNutritionCsv } = await import('./services/export-csv');
    exportNutritionCsv({ report });
  };

  const handleExportPdf = async () => {
    if (!report) return;
    const { exportNutritionPdf } = await import('./services/export-pdf');
    await exportNutritionPdf({ report });
  };

  const tabCounts = report
    ? report.tabs.reduce<{ [tabId: string]: { found: number; total: number } }>((acc, tab) => {
        const found = tab.geneGroups.reduce((s, g) => s + g.coverage.found, 0);
        const total = tab.geneGroups.reduce((s, g) => s + g.coverage.total, 0);
        acc[tab.tabId] = { found, total };
        return acc;
      }, {})
    : {};

  const availableTabs = report
    ? tabMetadata.filter((tab) => report.tabs.some((rt) => rt.tabId === tab.id))
    : [];
  const currentTab = report?.tabs.find((t) => t.tabId === activeTab);
  const currentMeta = tabMetadata.find((t) => t.id === activeTab);

  const isWorking = state === 'detecting' || state === 'parsing' || state === 'analyzing';

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-end" wrap="wrap">
          <Stack gap={4}>
            <Title order={2}>AI Genetics</Title>
            <Text size="sm" c="dimmed">
              Methylation, detox, and nutrition pathway analysis from raw genetic data
            </Text>
          </Stack>
        </Group>

        {state === 'idle' && <FileDropzone onFileAccepted={analyzeFile} />}

        {isWorking && (
          <Paper shadow="sm" p="md" radius="md">
            <Text fw={500} mb="xs">
              {progressMessage}
            </Text>
            <Progress value={progress} size="xl" radius="xl" striped animated />
          </Paper>
        )}

        {state === 'error' && (
          <Paper shadow="sm" p="md" radius="md">
            <Text c="red" fw={500} mb="md">
              An error occurred during analysis
            </Text>
            <Button onClick={reset}>Try Again</Button>
          </Paper>
        )}

        {state === 'complete' && report && currentTab && (
          <>
            <Paper shadow="xs" p="md" radius="md">
              <Group justify="space-between" wrap="wrap" gap="md">
                <Group gap="md" wrap="wrap">
                  <Text size="sm">
                    <strong>File:</strong> {report.fileInfo.fileName}
                  </Text>
                  <Text size="sm" c="dimmed">
                    <strong>Provider:</strong> {report.fileInfo.provider}
                  </Text>
                  <Text size="sm" c="dimmed">
                    <strong>Gender:</strong> {report.gender.detected} ({report.gender.confidence})
                  </Text>
                  <Text size="sm" c="dimmed">
                    <strong>Analyzed:</strong> {report.analysisDate.toLocaleDateString()}
                  </Text>
                </Group>
                <Group gap="xs">
                  <Button
                    variant="light"
                    size="sm"
                    leftSection={<IconDownload size={14} />}
                    onClick={handleExportCsv}
                  >
                    CSV
                  </Button>
                  <Button
                    size="sm"
                    leftSection={<IconDownload size={14} />}
                    onClick={handleExportPdf}
                  >
                    PDF
                  </Button>
                  <Button variant="subtle" size="sm" onClick={reset}>
                    New File
                  </Button>
                </Group>
              </Group>
            </Paper>

            <Group justify="space-between" align="flex-start">
              <Stack gap={4}>
                <Title order={3}>{currentMeta?.name ?? currentTab.tabName}</Title>
                {currentMeta?.description && (
                  <Text size="sm" c="dimmed">
                    {currentMeta.description}
                  </Text>
                )}
              </Stack>
              <Badge size="lg" color={coverageColor(currentTab.coverage.percentage)} variant="light">
                {currentTab.coverage.found}/{currentTab.coverage.total} variants (
                {currentTab.coverage.percentage}%)
              </Badge>
            </Group>

            <TabNavigation
              activeTab={activeTab}
              onTabChange={setActiveTab}
              tabMetadata={availableTabs}
              tabCounts={tabCounts}
            />

            <TabContent tabReport={currentTab} activeTab={activeTab} />
          </>
        )}
      </Stack>
    </Container>
  );
}
